import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Post } from "../../types/post.types";
import { useTranslation } from "react-i18next";

interface InfinitePostListProps {
    posts: Post[];
    onLoadMore: () => void;
    hasMore?: boolean;
    isLoading?: boolean;
    renderPost: (post: Post) => ReactNode;
}

export const InfinitePostList = ({
    posts,
    onLoadMore,
    hasMore = false,
    isLoading = false,
    renderPost,
}: InfinitePostListProps) => {
    const loadMoreRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const first = entries[0];
                if (first.isIntersecting && hasMore && !isLoading) {
                    onLoadMore();
                }
            },
            { threshold: 0.1 },
        );

        const currentRef = loadMoreRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [hasMore, isLoading, onLoadMore]);

    return (
        <Box>
            {posts.map(renderPost)}

            {/* Loading trigger */}
            {hasMore && (
                <Box ref={loadMoreRef} display="flex" justifyContent="center" alignItems="center" py={3}>
                    {isLoading && <CircularProgress size={24} />}
                </Box>
            )}

            {/* End of feed indicator */}
            {!hasMore && posts.length > 5 && (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    py={3}
                    sx={{
                        borderTop: "1px solid",
                        borderColor: "divider",
                        mt: 2,
                        color: "text.secondary",
                    }}
                >
                    {t("social.feed.end_of_feed", "You've reached the end of your feed")}
                </Box>
            )}
        </Box>
    );
};
